import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { eventBus } from '@/events/eventBus';
import { sendTopupReminder } from '@/domain/topupReminders';
import { isTopupPlanComplete } from '@/domain/topupPlan';
import { dayKey } from '@/utils/studioDay';
import type { TopupInstalment, TopupPlan } from '@/domain/types';

// Deterministic, sim-clock reconciler for pay-over-time top-up plans (tv-opw,
// building on tv-w98's model + helpers). Mirrors the sibling reconcilers
// (reconcileDepositHolds, reconcileSequenceClock): "now" is the sim clock, never
// Date.now(); it loops every active plan, skips settled records, and persists via
// upsertTopupPlan. Vault Diagram 02: a SYSTEM reminder fires on each instalment's
// due date; owed = total - paid; paid by the date -> advance, missed -> the Agent
// chase (tv-qta, which consumes the `missed` status + the `topup:missed` event).

function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}

/**
 * Advance every active top-up plan against the current sim clock. Per plan, act on
 * the CURRENT instalment (the earliest still `scheduled` or `reminded`):
 * - due + `scheduled`  -> fire the reminder, mark it `reminded` (remindedAt = now).
 * - `reminded` + the due day now strictly in the past -> mark it `missed` and emit
 *   `topup:missed` so tv-qta / reporting can pick up the chase.
 * A plan whose every instalment is `paid`/`cancelled` with nothing outstanding flips
 * to `completed`. Idempotent: a second call on the same clock is a no-op because the
 * status guards have already advanced. Compares on the calendar day (Melbourne +10)
 * so a reminder fires anywhere on its due date, not only after the due instant.
 */
export function reconcileTopupSchedule(): void {
  const brain = useBrainStore.getState();
  const now = nowIso();
  const nowDay = dayKey(now);

  for (const plan of Object.values(brain.topupPlans)) {
    if (plan.status !== 'active') continue;

    // The current instalment: the earliest not-yet-settled (scheduled or reminded).
    const currentIdx = plan.instalments.findIndex(
      (i) => i.status === 'scheduled' || i.status === 'reminded',
    );

    let instalments = plan.instalments;
    let missed: TopupInstalment | null = null;

    if (currentIdx !== -1) {
      const current = plan.instalments[currentIdx]!;
      const dueDay = dayKey(current.dueIso);
      if (current.status === 'scheduled' && dueDay <= nowDay) {
        // The reminder goes out on the due date; the status guard stops a re-fire.
        sendTopupReminder(plan.depositId);
        instalments = plan.instalments.map((inst, i) =>
          i === currentIdx ? { ...inst, status: 'reminded', remindedAt: now } : inst,
        );
      } else if (current.status === 'reminded' && nowDay > dueDay) {
        // The reminder went out on the due day; a later clock with no payment is the miss.
        instalments = plan.instalments.map((inst, i) =>
          i === currentIdx ? { ...inst, status: 'missed' } : inst,
        );
        missed = current;
      }
    }

    const instalmentsChanged = instalments !== plan.instalments;
    const nextPlan: TopupPlan = instalmentsChanged ? { ...plan, instalments } : plan;
    const status: TopupPlan['status'] = isTopupPlanComplete(nextPlan) ? 'completed' : plan.status;

    // Smallest correct mutation: only persist when something actually changed.
    if (!instalmentsChanged && status === plan.status) continue;
    brain.upsertTopupPlan({ ...nextPlan, status });

    if (missed) {
      eventBus.publish('topup:missed', {
        planId: plan.id,
        depositId: plan.depositId,
        customerId: plan.customerId,
        dueIso: missed.dueIso,
        at: now,
      });
    }
  }
}
